import React, { useReducer, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import MetricContext from './MetricsContext';
import {
  createUserMetric,
  getApiBaseUrl,
  getUserMetric,
  updateUserMetric as updateUserMetricRequest,
} from '../API/api';
import { sanitizeBrowserStorageValue } from '../utils';
import PageNames from '../utils/PageNames';
import ButtonNames from '../utils/ButtonNames';

// Define action types
const SET_USER_METRIC = 'SET_USER_METRIC';
const UPDATE_USER_METRIC = 'UPDATE_USER_METRIC';
const SET_ERROR = 'SET_ERROR';
const SET_LOADING = 'SET_LOADING';
const SESSION_ID_STORAGE_WHITELIST = /^[A-Za-z0-9._:-]{1,128}$/;
const DEFAULT_PAGE_TYPE = 'Default';
const UNKNOWN_LANGUAGE = 'Unknown';
const UNKNOWN_OUTPUT_TYPE = 'Unknown';

// Initial state
const initialState = {
  metric: null,
  error: null,
  loading: false, // Add loading state
};

function normalizeSessionId(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue || trimmedValue === 'undefined' || trimmedValue === 'null') {
    return null;
  }

  return SESSION_ID_STORAGE_WHITELIST.test(trimmedValue) ? trimmedValue : null;
}

function getStoredSessionId() {
  const rawSessionId = localStorage.getItem('sessionId');
  const sessionId = normalizeSessionId(rawSessionId);

  if (!sessionId && rawSessionId) {
    localStorage.removeItem('sessionId');
  }

  return sessionId;
}

async function parseMetricResponse(response, actionName) {
  if (typeof response === 'string') {
    return response;
  }

  const responseText = await response.text();
  const responsePreview = responseText.replaceAll(/\s+/g, ' ').trim().slice(0, 120);

  if (!response.ok) {
    const previewText = responsePreview ? `. Response preview: ${responsePreview}` : '';
    throw new Error(
      `${actionName} failed with ${response.status} ${response.statusText}${previewText}`
    );
  }

  if (!responseText.trim()) {
    return {};
  }

  try {
    return JSON.parse(responseText);
  } catch {
    const contentType = response.headers?.get?.('content-type') || 'unknown content type';
    const responseUrl = response.url || 'the metrics endpoint';

    throw new Error(
      `${actionName} expected JSON but received ${contentType} from ${responseUrl}. Response preview: ${responsePreview}`
    );
  }
}

function getStoredLocalMetrics() {
  const rawMetric = localStorage.getItem('localMetric');

  if (!rawMetric) {
    return [];
  }

  try {
    const parsedMetric = JSON.parse(rawMetric);
    return Array.isArray(parsedMetric) ? parsedMetric : [];
  } catch {
    return [];
  }
}

function enhanceMetricError(error, actionName) {
  if (error instanceof TypeError && /fetch/i.test(error.message)) {
    const apiBaseUrl = getApiBaseUrl() || window.location.origin;
    return new Error(
      `${actionName} could not reach the metrics API at ${apiBaseUrl}. Check the configured API base URL and confirm the backend is running.`
    );
  }

  return error;
}

// Reducer function
function reducer(state, action) {
  switch (action.type) {
    case SET_USER_METRIC:
    case UPDATE_USER_METRIC:
      return {
        ...state,
        metric: action.payload,
        error: null,
        loading: false,
      };
    case SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
}
const mergePages = (pages) => {
  const merged = {};
  pages.forEach((page) => {
    const { name, pageType, duration, interactions } = page;

    if (!merged[name]) {
      merged[name] = {
        name,
        pageType,
        duration: 0,
        interactions: [],
      };
    }
    if (duration) {
      merged[name].duration += duration; // Summing up the durations
    }

    if (interactions) {
      merged[name].interactions = merged[name].interactions.concat(interactions); // Merging interactions
    }
  });
  return Object.values(merged);
};

const buildMetricPage = (newInteraction) => ({
  ...newInteraction,
  name: newInteraction.name,
  interactions: newInteraction.interactions,
  pageType: newInteraction.pageType || DEFAULT_PAGE_TYPE,
});

const hasStartAssessmentInteraction = (page) =>
  page.interactions?.some((interaction) => interaction.name === ButtonNames.STARTASSESMENT);

const isStartAssessmentInteraction = (newInteraction) =>
  Array.isArray(newInteraction?.interactions) &&
  newInteraction.interactions.some((interaction) => interaction.name === ButtonNames.STARTASSESMENT);

const shouldCreateNewJourney = (lastJourney, newInteraction) =>
  Boolean(lastJourney?.pages?.some(hasStartAssessmentInteraction) && isStartAssessmentInteraction(newInteraction));

function buildNewAssessmentJourney(newInteraction, updateJourneyInformation) {
  const lang = localStorage.getItem('i18nextLng');

  return {
    startAppTime: new Date(),
    pages: [buildMetricPage(newInteraction)],
    language: lang || newInteraction.language || UNKNOWN_LANGUAGE,
    algorithmData: {
      questionsList: updateJourneyInformation.algorithmData?.questionsList || [],
      outputsList: updateJourneyInformation.algorithmData?.outputsList || [],
    },
    ...updateJourneyInformation,
  };
}

function buildFallbackJourney(newInteraction, updateJourneyInformation) {
  return {
    startAppTime: new Date(),
    pages: [buildMetricPage(newInteraction)],
    language: newInteraction.language || UNKNOWN_LANGUAGE,
    algorithmData: {
      questionsList: updateJourneyInformation.algorithmData?.questionsList || [],
      outputsList: updateJourneyInformation.algorithmData?.outputsList || [],
      outputType: updateJourneyInformation.algorithmData?.outputType || UNKNOWN_OUTPUT_TYPE,
    },
    ...updateJourneyInformation,
  };
}

function isMatchingMetricPage(page, newInteraction) {
  if (page.name === PageNames.QUESTIONS_PAGE) {
    return page.questionCode === newInteraction.questionCode;
  }

  return page.name === newInteraction.name;
}

function getUpdatedPages(lastJourney, newInteraction) {
  const updatedPages = [...(lastJourney.pages || [])];
  const pageIndex = updatedPages.findLastIndex((page) => isMatchingMetricPage(page, newInteraction));
  const existingPage = pageIndex !== -1 ? updatedPages[pageIndex] : null;
  const isLastPage = pageIndex === updatedPages.length - 1;

  if (existingPage && isLastPage) {
    const updatedInteractions = [...(existingPage.interactions || []), ...(newInteraction.interactions || [])];
    updatedPages[pageIndex] = { ...existingPage, ...newInteraction, interactions: updatedInteractions };
    return updatedPages;
  }

  if (!isLastPage) {
    return [...updatedPages, buildMetricPage(newInteraction)];
  }

  return updatedPages;
}

const getAlgorithmDataValue = (fieldName, lastAlgorithmData, incomingAlgorithmData) =>
  incomingAlgorithmData?.[fieldName] !== undefined
    ? incomingAlgorithmData[fieldName]
    : (lastAlgorithmData?.[fieldName] ?? []);

function buildMergedAlgorithmData(lastJourney, updateJourneyInformation) {
  const lastAlgorithmData = lastJourney.algorithmData || {};
  const incomingAlgorithmData = updateJourneyInformation.algorithmData || {};

  return {
    ...lastAlgorithmData,
    ...incomingAlgorithmData,
    questionsList: getAlgorithmDataValue('questionsList', lastAlgorithmData, incomingAlgorithmData),
    outputsList: getAlgorithmDataValue('outputsList', lastAlgorithmData, incomingAlgorithmData),
  };
}

function buildUpdatedLastJourney(lastJourney, newInteraction, updateJourneyInformation) {
  const durationFinalJourney = Math.abs(new Date() - new Date(lastJourney.startAppTime));

  return {
    ...lastJourney,
    pages: getUpdatedPages(lastJourney, newInteraction),
    endAppTime: new Date(),
    totalAppTime: durationFinalJourney,
    algorithmData: buildMergedAlgorithmData(lastJourney, updateJourneyInformation),
    ...updateJourneyInformation,
  };
}

function getUpdatedJourneys(activeMetric, newInteraction, updateJourneyInformation) {
  const updatedJourneys = [...(activeMetric?.journeys || [])];
  const lastJourneyIndex = updatedJourneys.length - 1;
  const lastJourney = updatedJourneys[lastJourneyIndex];

  if (shouldCreateNewJourney(lastJourney, newInteraction)) {
    return [...updatedJourneys, buildNewAssessmentJourney(newInteraction, updateJourneyInformation)];
  }

  if (lastJourney) {
    updatedJourneys[lastJourneyIndex] = buildUpdatedLastJourney(lastJourney, newInteraction, updateJourneyInformation);
    return updatedJourneys;
  }

  return [buildFallbackJourney(newInteraction, updateJourneyInformation)];
}

const buildUpdatedMetric = (activeMetric, newInteraction, updateJourneyInformation, newGeneralInformation) => ({
  ...activeMetric,
  journeys: getUpdatedJourneys(activeMetric, newInteraction, updateJourneyInformation),
  ...newGeneralInformation,
});

async function loadMetricForUpdate(activeMetric, metricSessionId, dispatch, setLoading) {
  if (activeMetric) {
    return { activeMetric, metricSessionId };
  }

  if (!metricSessionId) {
    return null;
  }

  setLoading(true);
  const response = await getUserMetric(metricSessionId);
  const loadedMetric = await parseMetricResponse(response, 'Fetching user metric');
  dispatch({ type: SET_USER_METRIC, payload: loadedMetric });

  return { activeMetric: loadedMetric, metricSessionId };
}

async function persistUpdatedMetric(updatedMetric, fallbackSessionId, dispatch) {
  const metricSessionId = normalizeSessionId(updatedMetric?.sessionId) || fallbackSessionId || getStoredSessionId();

  if (!metricSessionId) {
    return;
  }

  const response = await updateUserMetricRequest(metricSessionId, updatedMetric);
  const data = await parseMetricResponse(response, 'Updating user metric');

  if (typeof data === 'string') {
    return;
  }

  dispatch({ type: SET_USER_METRIC, payload: data });

  const responseSessionId = normalizeSessionId(data.sessionId) || metricSessionId;
  localStorage.setItem('sessionId', responseSessionId);
}

function MetricsContextProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setLoading = useCallback((isLoading) => {
    dispatch({ type: SET_LOADING, payload: isLoading });
  }, []);

  const storeLocalMetrics = useCallback((_newInteraction) => {
    // DISABLED
    // const localMetric = getStoredLocalMetrics();
    // const updatedMetrics = sanitizeBrowserStorageValue([...localMetric, _newInteraction]);
    // localStorage.setItem('localMetric', JSON.stringify(updatedMetrics));
  }, []);

  const initMetrics = useCallback(async () => {
    // DISABLED
    // setLoading(true);
    // try {
    //   const sessionId = getStoredSessionId();
    //   const lang = localStorage.getItem('i18nextLng');
    //   const modeOfAccess = localStorage.getItem('source');
    //   const langEle = lang ? { language: lang } : {};
    //   const localMetric = mergePages(getStoredLocalMetrics());
    //   if (sessionId) {
    //     const response = await getUserMetric(sessionId);
    //     const data = await parseMetricResponse(response, 'Fetching user metric');
    //     dispatch({ type: SET_USER_METRIC, payload: data });
    //   } else {
    //     const id = `user${uuid()}`;
    //     const response = await createUserMetric({
    //       sessionId: id,
    //       journeys: [
    //         {
    //           startAppTime: new Date(),
    //           pages: [...localMetric],
    //           deviceData: {
    //             source: modeOfAccess,
    //           },
    //           ...langEle,
    //         },
    //       ],
    //     });
    //     const data = await parseMetricResponse(response, 'Creating user metric');
    //     if (typeof data !== 'string') {
    //       localStorage.setItem('sessionId', id);
    //       dispatch({ type: SET_USER_METRIC, payload: data });
    //     }
    //   }
    // } catch (error) {
    //   const normalizedError = enhanceMetricError(error, 'Fetching or creating user metric');
    //   dispatch({ type: SET_ERROR, payload: normalizedError });
    //   console.error('Error fetching or creating user metric:', normalizedError);
    // } finally {
    //   setLoading(false);
    // }
  }, [setLoading]);

  const updateUserMetric = useCallback(
    async (_newInteraction, _updateJourneyInformation = {}, _newGeneralInformation = {}) => {
      // DISABLED
      // try {
      //   const storedSessionId = normalizeSessionId(state.metric?.sessionId) || getStoredSessionId();
      //   const metricForUpdate = await loadMetricForUpdate(state.metric, storedSessionId, dispatch, setLoading);
      //   if (!metricForUpdate) {
      //     return;
      //   }
      //   const updatedMetric = buildUpdatedMetric(
      //     metricForUpdate.activeMetric,
      //     _newInteraction,
      //     _updateJourneyInformation,
      //     _newGeneralInformation
      //   );
      //   dispatch({ type: UPDATE_USER_METRIC, payload: updatedMetric });
      //   setLoading(true);
      //   await persistUpdatedMetric(updatedMetric, metricForUpdate.metricSessionId, dispatch);
      // } catch (error) {
      //   const normalizedError = enhanceMetricError(error, 'Updating user metric');
      //   dispatch({ type: SET_ERROR, payload: normalizedError });
      //   console.error('Error updating user metric:', normalizedError);
      // } finally {
      //   setLoading(false);
      // }
    },
    [state.metric, setLoading]
  );

  return (
    <MetricContext.Provider
      value={useMemo(
        () => ({
          ...state,
          updateUserMetric,
          initMetrics,
          storeLocalMetrics,
        }),
        [state, updateUserMetric, initMetrics, storeLocalMetrics]
      )}
    >
      {children}
    </MetricContext.Provider>
  );
}

MetricsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MetricsContextProvider;
